import { createTheme } from '@mui/material/styles';

// Design tokens — single source of truth for colors / spacing / radius.
// Migration: prefer `theme.palette.brand.*`, `theme.shape.radius.*`, and
// `theme.spacing(n)` over inline hex codes / pixel values.
export const brandTokens = {
  // Brand identity — the deep blue users see in the auth hero, ToS link, etc.
  primary: '#22489E',
  primaryDark: '#1A3678',
  primaryLight: '#4666B5',
  // Wellness accent — soft teal used in toast successes and accents.
  accent: '#0F766E',
  accentLight: '#14B8A6',
  // Surfaces
  surfaceMuted: '#F8FAFC',
  surfaceBorder: '#CBD5E1',
};

export const radiusTokens = {
  sm: 8,
  md: 12,
  lg: 20,
  xl: 24,
  pill: 9999,
};

// Canonical element identity colors. These were previously redefined ad hoc in
// many files (HomePage, the element pages, TodayFocusCard, quizzes) and drifted
// — Earth alone appeared as #10b981 / #22c55e / #00a86b / #064e3b. Centralizing
// keeps the intentional element identity while killing the drift. Import via
// `theme.palette.elements.earth.*`.
export const elementTokens = {
  earth: { main: '#10b981', light: '#d1fae5', dark: '#047857' },
  water: { main: '#0ea5e9', light: '#e0f2fe', dark: '#0369a1' },
  air:   { main: '#8b5cf6', light: '#ede9fe', dark: '#6d28d9' },
  fire:  { main: '#f97316', light: '#ffedd5', dark: '#c2410c' },
};

// Named hero gradients. Gradients should be rare/high-impact; reserve these few
// instead of scattering bespoke conic/radial gradients on small UI. Import via
// `theme.palette.gradients.brandHero`.
export const gradientTokens = {
  brandHero: `linear-gradient(135deg, ${brandTokens.primary} 0%, ${brandTokens.primaryDark} 100%)`,
  accentHero: `linear-gradient(135deg, ${brandTokens.accentLight} 0%, ${brandTokens.accent} 100%)`,
  earth: `linear-gradient(135deg, ${elementTokens.earth.main} 0%, ${elementTokens.earth.dark} 100%)`,
  water: `linear-gradient(135deg, ${elementTokens.water.main} 0%, ${elementTokens.water.dark} 100%)`,
  air: `linear-gradient(135deg, ${elementTokens.air.main} 0%, ${elementTokens.air.dark} 100%)`,
  fire: `linear-gradient(135deg, ${elementTokens.fire.main} 0%, ${elementTokens.fire.dark} 100%)`,
};

export const themeOptions = {
  shape: {
    borderRadius: radiusTokens.sm, // Reduced from 16 to be less aggressive
    radius: radiusTokens, // theme.shape.radius.md, etc.
  },
  // Calm, restrained elevation. Earlier this ramped to `0 50px 130px / 0.75`
  // (near-black) — far heavier than a wellness product should feel. Now a
  // gentle progression that plateaus; high indices stay subtle instead of
  // becoming dramatic drop shadows. Must remain 25 entries (MUI contract).
  shadows: [
    'none',
    '0 1px 2px rgba(16,24,40,0.04)',
    '0 2px 4px rgba(16,24,40,0.05)',
    '0 3px 6px rgba(16,24,40,0.06)',
    '0 4px 8px rgba(16,24,40,0.06)',
    '0 5px 10px rgba(16,24,40,0.07)',
    '0 6px 12px rgba(16,24,40,0.07)',
    '0 8px 16px rgba(16,24,40,0.08)',
    '0 10px 20px rgba(16,24,40,0.08)',
    '0 12px 22px rgba(16,24,40,0.09)',
    '0 13px 24px rgba(16,24,40,0.09)',
    '0 14px 26px rgba(16,24,40,0.10)',
    '0 15px 28px rgba(16,24,40,0.10)',
    '0 16px 30px rgba(16,24,40,0.10)',
    '0 17px 32px rgba(16,24,40,0.11)',
    '0 18px 34px rgba(16,24,40,0.11)',
    '0 19px 36px rgba(16,24,40,0.11)',
    '0 20px 38px rgba(16,24,40,0.12)',
    '0 21px 40px rgba(16,24,40,0.12)',
    '0 22px 42px rgba(16,24,40,0.12)',
    '0 22px 44px rgba(16,24,40,0.13)',
    '0 23px 46px rgba(16,24,40,0.13)',
    '0 23px 47px rgba(16,24,40,0.13)',
    '0 24px 48px rgba(16,24,40,0.14)',
    '0 24px 48px rgba(16,24,40,0.14)',
  ],
  // Explicit type scale. Previously only fontFamily + heading weights were
  // set, so body/subtitle sizing fell back to MUI defaults and pages patched
  // sizes ad hoc (the `!important` font-size soup). h1–h3 defaults are huge
  // (up to 6rem) and rarely used — tamed here. h4–h6 sizes are intentionally
  // left at MUI defaults so existing page titles don't shift.
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', lineHeight: 1.15, letterSpacing: '-0.02em', fontWeight: 800 },
    h2: { fontSize: '2rem', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 800 },
    h3: { fontSize: '1.625rem', lineHeight: 1.25, letterSpacing: '-0.02em', fontWeight: 800 },
    h4: { letterSpacing: '-0.02em', fontWeight: 700, lineHeight: 1.3 },
    h5: { letterSpacing: '-0.01em', fontWeight: 700, lineHeight: 1.35 },
    h6: { letterSpacing: '-0.01em', fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: '1rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    button: { fontWeight: 700, letterSpacing: '0.01em' },
    caption: { letterSpacing: '0.08em', textTransform: 'uppercase' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 700,
          // Flat by default — only contained buttons carry elevation. No
          // hover "lift": calmer, and text/outlined buttons no longer float.
          boxShadow: 'none',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
          touchAction: 'manipulation'
        },
        contained: {
          boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(16,24,40,0.10)'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:active': {
            transform: 'scale(0.92)'
          },
          touchAction: 'manipulation'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 4px 12px rgba(16,24,40,0.05)',
          // No translate on hover — a gentle shadow change reads calmer and
          // avoids the whole grid "jumping" as the pointer moves across it.
          transition: 'box-shadow 0.25s ease',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(16,24,40,0.08)',
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8, // Reduced from 16
          // Exclude navigation bars from rounded corners
          '&.MuiAppBar-root': {
            borderRadius: '0 !important',
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: '0 !important',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1) !important', // Keep original shadow
          margin: '0 !important',
          padding: '0 !important',
          top: '0 !important',
          left: '0 !important',
          right: '0 !important',
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          borderRadius: '0 !important',
          margin: '0 !important',
          padding: '0 16px !important', // Keep some padding but no top/bottom margin
        }
      }
    },
    MuiDialog: {
      defaultProps: {
        transitionDuration: 400,
      },
      styleOverrides: {
        paper: {
          borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          // Brand-blue focus ring (was generic MUI blue rgba(25,118,210)).
          '&.Mui-focused': {
            boxShadow: '0 0 0 4px rgba(34, 72, 158, 0.15)',
          }
        }
      }
    }
  },
  palette: {
    // Primary IS the brand now. Previously this was generic Material blue
    // (#1976d2) while the real FuseSoul blue sat unused in `brand.*`, so every
    // `color="primary"` element rendered off-brand. Secondary aligns to the
    // wellness teal (was generic purple #9c27b0).
    primary: {
      main: brandTokens.primary,      // #22489E
      light: brandTokens.primaryLight, // #4666B5
      dark: brandTokens.primaryDark,   // #1A3678
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brandTokens.accent,        // #0F766E teal
      light: brandTokens.accentLight,  // #14B8A6
      dark: '#0B5C56',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    warning: {
      main: '#ed6c02',
      light: '#ff9800',
      dark: '#e65100',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    // Brand tokens — use these for surfaces that should feel "FuseSoul" rather
    // than generic MUI. Kept separate from `primary` so existing
    // color="primary" behavior is unchanged.
    brand: {
      main: brandTokens.primary,
      dark: brandTokens.primaryDark,
      light: brandTokens.primaryLight,
      accent: brandTokens.accent,
      accentLight: brandTokens.accentLight,
      surfaceMuted: brandTokens.surfaceMuted,
      surfaceBorder: brandTokens.surfaceBorder,
      contrastText: '#FFFFFF',
    },
    // Canonical element identity + named hero gradients (see token defs above).
    elements: elementTokens,
    gradients: gradientTokens,
  },
};

const theme = createTheme(themeOptions);

export default theme;
